/**
 * Quick find (N4) — pure model: what to offer for a query.
 * Every destination is a URL now (N3), so every result is just an href.
 */
import { paths } from '../../routes/paths';

export type QuickFindGroup = 'Go to' | 'Open by number' | 'Your documents';

export interface QuickFindItem {
  id: string;
  group: QuickFindGroup;
  label: string;
  detail?: string;
  href: string;
  /** Extra words to match on (lower-case). */
  keywords?: string;
}

export interface CommandOptions {
  role?: string;
  slug: string;
  quotesEnabled?: boolean;
  canPlaceOrders?: boolean;
  /** Tenant has orders on (documentFeatures.ordersEnabled). Default true. */
  ordersEnabled?: boolean;
  /** Show developer-only pages (Admin tests). Defaults to dev builds only; the page itself is not in production bundles. */
  includeDevTools?: boolean;
}

const ADMIN_TABS: Record<'glbadm' | 'rsladm' | 'tntadm', Array<[string, string]>> = {
  glbadm: [['dashboard', 'Dashboard'], ['tenants', 'Tenants'], ['resellers', 'Resellers'], ['reseller-queue', 'Reseller queue'],
    ['users', 'Users'], ['sessions', 'Active sessions'], ['audit-log', 'Audit log'], ['support', 'Support'], ['billing', 'Billing'],
    ['extensions', 'Extensions']],
  rsladm: [['dashboard', 'Dashboard'], ['tenants', 'Tenants'], ['audit-log', 'Audit log'], ['support', 'Support'], ['settings', 'Settings']],
  tntadm: [['dashboard', 'Dashboard'], ['users', 'Users'], ['registrations', 'Registrations'], ['settings', 'Settings'],
    ['audit-log', 'Audit log'], ['api-log', 'API log'], ['support', 'Support'], ['billing', 'Billing']],
};

/** Developer-only pages, listed only when includeDevTools is on (matches the nav, which hides them outside dev builds). */
const DEV_TABS: Partial<Record<'glbadm' | 'rsladm' | 'tntadm', Array<[string, string]>>> = {
  glbadm: [['admin-tests', 'Admin tests']],
};

const AREA_LABEL = { glbadm: 'Global admin', rsladm: 'Reseller admin', tntadm: 'Tenant admin' } as const;

/** Static "go to" commands for the signed-in user's role. */
export function buildCommands(opts: CommandOptions): QuickFindItem[] {
  const { role, slug } = opts;
  const devTools = opts.includeDevTools ?? import.meta.env.DEV;
  const areas: Array<'glbadm' | 'rsladm' | 'tntadm'> =
    role === 'global_admin' ? ['glbadm', 'tntadm'] : role === 'reseller_admin' ? ['rsladm', 'tntadm'] : role === 'tenant_admin' ? ['tntadm'] : [];

  if (areas.length > 0) {
    return areas.flatMap(area => [...ADMIN_TABS[area], ...(devTools ? DEV_TABS[area] ?? [] : [])].map(([tab, label]) => ({
      id: `cmd:${area}:${tab}`,
      group: 'Go to' as const,
      label,
      detail: AREA_LABEL[area],
      href: paths.admin(area, tab),
      keywords: `${area} ${AREA_LABEL[area].toLowerCase()} ${tab.replace(/-/g, ' ')}`,
    })));
  }

  const items: QuickFindItem[] = [{ id: 'cmd:home', group: 'Go to', label: 'Dashboard', href: paths.home(), keywords: 'home orders invoices' }];
  if (opts.canPlaceOrders !== false) {
    if (opts.ordersEnabled !== false) {items.push({ id: 'cmd:new-order', group: 'Go to', label: 'New order', href: paths.newOrder(slug), keywords: 'create place order' });}
    if (opts.quotesEnabled) {items.push({ id: 'cmd:new-quote', group: 'Go to', label: 'New quote', href: paths.newQuote(slug), keywords: 'quotation estimate' });}
  }
  items.push(
    { id: 'cmd:my-account', group: 'Go to', label: 'My account', href: paths.myAccount(slug), keywords: 'account statement balance profile' },
    { id: 'cmd:settings', group: 'Go to', label: 'Settings', href: paths.settings(slug), keywords: 'security password two-step preferences' },
    { id: 'cmd:support', group: 'Go to', label: 'Support tickets', href: paths.support(slug), keywords: 'help ticket' },
  );
  return items;
}

type DocKind = 'order' | 'invoice' | 'shipment' | 'credit-memo' | 'quote';

const KIND_LABEL: Record<DocKind, string> = {
  order: 'order', invoice: 'invoice', shipment: 'shipment', 'credit-memo': 'credit memo', quote: 'quote',
};

/** Common Business Central number-series prefixes, used only to rank the guesses. */
const PREFIX_HINTS: Array<[RegExp, DocKind]> = [
  [/^(PCM|CM|SCM)/i, 'credit-memo'],
  [/^(PSI|SI|INV|PS-INV)/i, 'invoice'],
  [/^(PSS|SH|SHP|PS-SHP)/i, 'shipment'],
  [/^(SQ|QU|QT|S-QUO)/i, 'quote'],
  [/^(SO|S-ORD|ORD)/i, 'order'],
];

/** A query that looks like a document number: 3–40 chars, has a digit, no spaces at the ends. */
export function looksLikeDocumentNo(q: string): boolean {
  const t = q.trim();
  return t.length >= 3 && t.length <= 40 && /\d/.test(t) && /^[A-Za-z0-9][A-Za-z0-9\-/_.]*$/.test(t);
}

/** "Open order SO-1001", "Open invoice SO-1001", … — best guess first. */
export function documentJumps(query: string, opts: { slug: string; role?: string; quotesEnabled?: boolean }): QuickFindItem[] {
  if (opts.role && opts.role !== 'user') {return [];}
  if (!looksLikeDocumentNo(query)) {return [];}
  const no = query.trim().toUpperCase();
  const kinds: DocKind[] = ['order', 'invoice', 'shipment', 'credit-memo'];
  if (opts.quotesEnabled) {kinds.push('quote');}
  const hinted = PREFIX_HINTS.find(([re]) => re.test(no))?.[1];
  if (hinted && kinds.includes(hinted)) {kinds.sort((a, b) => (a === hinted ? -1 : b === hinted ? 1 : 0));}
  const hrefFor = (k: DocKind): string => {
    switch (k) {
      case 'order': return paths.order(opts.slug, no);
      case 'invoice': return paths.invoice(opts.slug, no);
      case 'shipment': return paths.shipment(opts.slug, no);
      case 'credit-memo': return paths.creditMemo(opts.slug, no);
      case 'quote': return paths.quote(opts.slug, no);
    }
  };
  return kinds.map(k => ({ id: `jump:${k}:${no}`, group: 'Open by number', label: `Open ${KIND_LABEL[k]} ${no}`, href: hrefFor(k) }));
}

/** A customer's document, normalised for search. */
export interface IndexedDocument {
  kind: DocKind;
  no: string;
  /** Customer's PO / external document number. */
  reference?: string;
  date?: string;
  status?: string;
  /** Sales order an invoice came from. */
  orderNo?: string;
}

const s = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const pick = (r: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) { const v = s(r[k]); if (v) {return v;} }
  return '';
};

/** Normalise the shapes BCDataService returns (field names vary by endpoint/version). */
export function toIndexedDocument(kind: DocKind, r: Record<string, unknown>): IndexedDocument | null {
  const no = kind === 'order' ? pick(r, ['documentNo', 'number', 'orderNo', 'no'])
    : kind === 'invoice' ? pick(r, ['invoiceNo', 'number', 'documentNo', 'no'])
      : kind === 'shipment' ? pick(r, ['shipmentNo', 'number', 'documentNo', 'no'])
        : kind === 'credit-memo' ? pick(r, ['creditMemoNo', 'number', 'documentNo', 'no'])
          : pick(r, ['quoteNo', 'documentNo', 'number', 'no']);
  if (!no) {return null;}
  return {
    kind,
    no,
    reference: pick(r, ['poReference', 'externalDocumentNo', 'externalDocumentNumber', 'yourReference']) || undefined,
    date: pick(r, ['orderDate', 'postingDate', 'documentDate', 'shipmentDate', 'date']).slice(0, 10) || undefined,
    status: pick(r, ['status']) || undefined,
    orderNo: kind === 'invoice' ? (pick(r, ['orderNo']) || undefined) : undefined,
  };
}

/** Substring match on number, PO reference and source order; exact/prefix number matches first. */
export function searchDocuments(docs: IndexedDocument[], query: string, slug: string, limit = 8): QuickFindItem[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) {return [];}
  const scored: Array<[number, IndexedDocument]> = [];
  for (const d of docs) {
    const no = d.no.toLowerCase();
    const ref = (d.reference ?? '').toLowerCase();
    const ord = (d.orderNo ?? '').toLowerCase();
    let score = -1;
    if (no === q) {score = 0;} else if (no.startsWith(q)) {score = 1;} else if (no.includes(q)) {score = 2;} else if (ref.includes(q)) {score = 3;} else if (ord.includes(q)) {score = 4;}
    if (score >= 0) {scored.push([score, d]);}
  }
  scored.sort((a, b) => a[0] - b[0] || (b[1].date ?? '').localeCompare(a[1].date ?? ''));
  return scored.slice(0, limit).map(([, d]) => {
    const href = d.kind === 'order' ? paths.order(slug, d.no)
      : d.kind === 'invoice' ? paths.invoice(slug, d.no, d.orderNo)
        : d.kind === 'shipment' ? paths.shipment(slug, d.no)
          : d.kind === 'credit-memo' ? paths.creditMemo(slug, d.no)
            : paths.quote(slug, d.no);
    const label = `${KIND_LABEL[d.kind][0].toUpperCase()}${KIND_LABEL[d.kind].slice(1)} ${d.no}`;
    const detail = [d.reference && `PO ${d.reference}`, d.status, d.date].filter(Boolean).join(' · ') || undefined;
    return { id: `doc:${d.kind}:${d.no}`, group: 'Your documents' as const, label, detail, href };
  });
}

/** Filter "go to" commands by label/keywords; empty query → all. */
export function filterCommands(items: QuickFindItem[], query: string): QuickFindItem[] {
  const q = query.trim().toLowerCase();
  if (!q) {return items;}
  const words = q.split(/\s+/);
  return items.filter(i => {
    const hay = `${i.label} ${i.detail ?? ''} ${i.keywords ?? ''}`.toLowerCase();
    return words.every(w => hay.includes(w));
  });
}
